import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import * as gastosApi from '@/api/gastos.api';
import type { GastosConfig, ServicioExterno } from '@/api/gastos.api';

/**
 * Gastos y APIs (solo OWNER): lo que cuesta este sistema al mes, lo que paga
 * el cliente, la ganancia que queda y el estado de cada servicio externo.
 * La IA sale medida; los gastos fijos y el cobro se anotan aquí.
 */

function usd(n: number): string {
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '−' : ''}US$${s}`;
}

const SUGERIDOS = ['Servidor', 'Dominio', 'WhatsApp', 'Correo'];

const ESTADO: Record<ServicioExterno['estado'], { label: string; variant: 'success' | 'default' | 'danger' }> = {
  ok: { label: 'Funciona', variant: 'success' },
  apagada: { label: 'Apagada', variant: 'default' },
  error: { label: 'Con error', variant: 'danger' },
};

const inputCls =
  'w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900';

function Cifra({ titulo, valor, nota, tono }: { titulo: string; valor: string; nota?: string; tono?: 'bien' | 'mal' }) {
  return (
    <Card>
      <CardBody>
        <p className="text-sm text-slate-500 dark:text-slate-400">{titulo}</p>
        <p
          className={cn(
            'text-xl font-bold tabular-nums sm:text-2xl text-slate-900 dark:text-slate-100',
            tono === 'bien' && 'text-green-700 dark:text-green-300',
            tono === 'mal' && 'text-red-700 dark:text-red-300',
          )}
        >
          {valor}
        </p>
        {nota && <p className="text-xs text-slate-400">{nota}</p>}
      </CardBody>
    </Card>
  );
}

export default function Gastos() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['gastos'], queryFn: gastosApi.getGastos });
  const [draft, setDraft] = useState<GastosConfig | null>(null);
  const config = draft ?? data?.config ?? { cobroMensualUsd: null, fijos: [] };

  const save = useMutation({
    mutationFn: () =>
      gastosApi.putGastos({ ...config, fijos: config.fijos.filter((f) => f.nombre.trim()) }),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['gastos'] });
      toast.success('Gastos guardados');
    },
    onError: () => toast.error('No se pudieron guardar los gastos'),
  });

  const setFijo = (i: number, campo: 'nombre' | 'usdMes', valor: string) => {
    const fijos = config.fijos.map((f, j) =>
      j === i ? { ...f, [campo]: campo === 'usdMes' ? Math.max(0, Number(valor) || 0) : valor } : f,
    );
    setDraft({ ...config, fijos });
  };

  if (isError) {
    return (
      <div className="space-y-3 p-6 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-300">No se pudieron cargar los gastos.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={28} />
      </div>
    );
  }

  const { cuenta, ia, servicios } = data;
  const faltan = SUGERIDOS.filter((n) => !config.fijos.some((f) => f.nombre.toLowerCase() === n.toLowerCase()));

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Gastos y APIs"
        description="Lo que cuesta este sistema al mes, lo que paga el cliente y cómo está cada servicio conectado. Solo lo ve la administración."
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <Cifra
          titulo="Paga el cliente"
          valor={data.config.cobroMensualUsd == null ? 'Sin anotar' : usd(data.config.cobroMensualUsd)}
          nota="Al mes"
        />
        <Cifra titulo="Gastos fijos" valor={usd(cuenta.fijosUsd)} nota={`${data.config.fijos.length} ${data.config.fijos.length === 1 ? 'anotado' : 'anotados'}`} />
        <Cifra
          titulo="IA (últimos 30 días)"
          valor={usd(cuenta.iaUsd)}
          nota={`${ia.turnosApi} turnos${ia.usosCli ? ` · ${ia.usosCli} por suscripción` : ''}`}
        />
        <Cifra
          titulo="Ganancia"
          valor={cuenta.gananciaUsd == null ? '—' : usd(cuenta.gananciaUsd)}
          nota={
            cuenta.gananciaUsd == null
              ? 'Anota lo que paga el cliente'
              : `Gastos ${usd(cuenta.totalUsd)} · margen ${cuenta.margenPct ?? 0} %`
          }
          tono={cuenta.gananciaUsd == null ? undefined : cuenta.gananciaUsd >= 0 ? 'bien' : 'mal'}
        />
      </div>

      {ia.modelosSinPrecio.length > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Modelos de IA sin precio (su gasto no está sumado): {ia.modelosSinPrecio.join(', ')}.{' '}
          <Link to="/consumos" className="underline">
            Ponerles precio en Consumos
          </Link>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardBody className="space-y-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Cobro y gastos fijos</p>
            <label className="block space-y-1">
              <span className="text-xs text-slate-500">Lo que paga el cliente al mes (US$)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={1}
                value={config.cobroMensualUsd ?? ''}
                placeholder="Sin acordar"
                onChange={(e) =>
                  setDraft({
                    ...config,
                    cobroMensualUsd: e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0),
                  })
                }
                className={inputCls}
              />
            </label>

            <div className="space-y-2">
              <span className="text-xs text-slate-500">Gastos fijos al mes (US$)</span>
              {config.fijos.length === 0 && (
                <p className="text-sm text-slate-400">Ninguno todavía. Añade el servidor, el dominio, WhatsApp…</p>
              )}
              {config.fijos.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    aria-label="Nombre del gasto"
                    value={f.nombre}
                    maxLength={60}
                    onChange={(e) => setFijo(i, 'nombre', e.target.value)}
                    className={inputCls}
                  />
                  <input
                    aria-label={`Monto de ${f.nombre || 'este gasto'}`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={0.01}
                    value={f.usdMes}
                    onChange={(e) => setFijo(i, 'usdMes', e.target.value)}
                    className={cn(inputCls, 'w-28 shrink-0')}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Quitar ${f.nombre || 'este gasto'}`}
                    onClick={() => setDraft({ ...config, fijos: config.fijos.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                {faltan.map((n) => (
                  <Button
                    key={n}
                    variant="outline"
                    size="sm"
                    onClick={() => setDraft({ ...config, fijos: [...config.fijos, { nombre: n, usdMes: 0 }] })}
                  >
                    <Plus className="h-3.5 w-3.5" /> {n}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDraft({ ...config, fijos: [...config.fijos, { nombre: '', usdMes: 0 }] })}
                >
                  <Plus className="h-3.5 w-3.5" /> Otro
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              {draft && (
                <Button variant="outline" onClick={() => setDraft(null)}>
                  Descartar
                </Button>
              )}
              <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!draft}>
                Guardar
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Servicios conectados (APIs)</p>
              <Link to="/ajustes/clave-ia" className="text-xs text-brand-600 underline dark:text-brand-300">
                Clave de IA
              </Link>
            </div>
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {servicios.map((s, i) => (
                <li key={i} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {s.nombre} <span className="text-xs font-normal text-slate-400">· {s.tipo}</span>
                    </p>
                    <p className="break-words text-xs text-slate-500">{s.detalle}</p>
                    {s.ultimoUsoAt && (
                      <p className="text-xs text-slate-400">Último uso {relativeTime(s.ultimoUsoAt)}</p>
                    )}
                  </div>
                  <Badge variant={ESTADO[s.estado].variant} className="shrink-0">
                    {ESTADO[s.estado].label}
                  </Badge>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400">
              Más detalle en <Link to="/sistema" className="underline">Estado del sistema</Link> y{' '}
              <Link to="/consumos" className="underline">Consumos</Link>.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
